import { Link } from "react-router-dom";
import { BadgeCheck, Star, Heart, MapPin, ShoppingBag } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";
import BusinessLogo from "@/components/BusinessLogo";

/**
 * Blinterest V2 card variants. Each variant renders a compact "Discover
 * card" tuned for a specific content type. The parent (BlinterestHomePage)
 * picks the variant based on the section — e.g. `ProductCard` for Trending,
 * `BusinessCard` for Businesses to Know, `FoodExperienceCard` for Near You.
 *
 * All cards share:
 *  - object-fit: cover images with fixed aspect ratios (no distortion)
 *  - a Keep (Save) affordance where relevant
 *  - a link back to the source business / pin
 *  - data-testid based on the pin_id or business_id for QA
 */

const currency = (v) => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toFixed(2)}`;
};

// ---- Product / Trending / Made For You card ----------------------------
export function ProductCard({ pin, onSave, className = "" }) {
  const biz = pin?.business;
  const price = currency(pin?.price);
  const rating = biz?.bc_score ? Number(biz.bc_score).toFixed(1) : null;
  return (
    <Link
      to={`/blinterest/pin/${pin.pin_id}`}
      className={`group block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors ${className}`}
      data-testid={`product-card-${pin.pin_id}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-white/5">
        <img
          src={resolveAssetUrl(pin.image_url)}
          alt={pin.title || "Find"}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
        />
        {onSave && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSave(pin); }}
            className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 hover:bg-white text-tbc-navy flex items-center justify-center shadow-sm opacity-90"
            aria-label="Keep this Find"
            data-testid={`product-card-save-${pin.pin_id}`}
          >
            <Heart className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="p-2.5 sm:p-3">
        <p className="text-white font-body text-sm line-clamp-1">{pin.title}</p>
        {biz && (
          <p className="text-white/60 font-body text-xs mt-0.5 flex items-center gap-1 truncate">
            <span className="truncate">{biz.name}</span>
            {biz.is_verified && <BadgeCheck className="w-3 h-3 text-tbc-gold flex-none" />}
          </p>
        )}
        <div className="flex items-center justify-between mt-1.5">
          {price ? (
            <span className="font-heading text-tbc-gold text-sm">{price}</span>
          ) : (
            <span />
          )}
          {rating && (
            <span className="inline-flex items-center gap-0.5 text-tbc-gold text-xs">
              <Star className="w-3 h-3 fill-tbc-gold" />
              {rating}
              {biz?.review_count > 0 && (
                <span className="text-white/50 ml-0.5">({biz.review_count})</span>
              )}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ---- Business card -----------------------------------------------------
export function BusinessCard({ business, className = "" }) {
  if (!business) return null;
  return (
    <Link
      to={`/business/${business.business_id}`}
      className={`group block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors ${className}`}
      data-testid={`business-card-${business.business_id}`}
    >
      <div className="p-3 flex flex-col items-center text-center gap-2">
        <BusinessLogo
          name={business.name}
          logoUrl={business.logo_url}
          size="lg"
          shape="rounded"
        />
        <p className="font-heading text-white text-sm line-clamp-1 flex items-center gap-1">
          <span className="truncate">{business.name}</span>
          {business.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-tbc-gold flex-none" />}
        </p>
        {business.category && (
          <p className="text-white/50 text-[10px] uppercase tracking-widest">
            {business.category}
          </p>
        )}
        {business.bc_score != null && (
          <p className="inline-flex items-center gap-0.5 text-tbc-gold text-xs">
            <Star className="w-3 h-3 fill-tbc-gold" />
            {Number(business.bc_score).toFixed(1)}
            {business.review_count > 0 && (
              <span className="text-white/50 ml-0.5">({business.review_count})</span>
            )}
          </p>
        )}
      </div>
    </Link>
  );
}

// ---- Food / Experience card (Near You) --------------------------------
export function FoodExperienceCard({ business, className = "" }) {
  if (!business) return null;
  const cover = business.cover_image || business.logo_url;
  return (
    <Link
      to={`/business/${business.business_id}`}
      className={`group block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors ${className}`}
      data-testid={`near-you-card-${business.business_id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-white/5">
        {cover ? (
          <img
            src={resolveAssetUrl(cover)}
            alt={business.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-tbc-gold/20" />
        )}
      </div>
      <div className="p-2.5">
        <p className="text-white font-body text-sm line-clamp-1 flex items-center gap-1">
          <span className="truncate">{business.name}</span>
          {business.is_verified && <BadgeCheck className="w-3 h-3 text-tbc-gold flex-none" />}
        </p>
        {(business.city || business.state) && (
          <p className="text-white/60 font-body text-xs mt-0.5 inline-flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 text-white/50" />
            {[business.city, business.state].filter(Boolean).join(", ")}
          </p>
        )}
      </div>
    </Link>
  );
}

// ---- Shop Black — real product card ------------------------------------
export function ShopBlackCard({ product, className = "" }) {
  const image = product?.images?.[0];
  const price = currency(product?.price);
  const rating = product?.rating ? Number(product.rating).toFixed(1)
                                 : (product?.business?.bc_score ? Number(product.business.bc_score).toFixed(1) : null);
  return (
    <Link
      to={`/product/${product.product_id}`}
      className={`group block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors ${className}`}
      data-testid={`shop-black-card-${product.product_id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-white/5">
        {image ? (
          <img
            src={resolveAssetUrl(image)}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-tbc-gold/10" />
        )}
      </div>
      <div className="p-2.5">
        <p className="text-white font-body text-sm line-clamp-1">{product.name}</p>
        {product?.business && (
          <p className="text-white/60 text-xs mt-0.5 flex items-center gap-1 truncate">
            <span className="truncate">{product.business.name}</span>
            {product.business.is_verified && <BadgeCheck className="w-3 h-3 text-tbc-gold flex-none" />}
          </p>
        )}
        <div className="flex items-center justify-between mt-1.5">
          {price && <span className="font-heading text-tbc-gold text-sm">{price}</span>}
          <div className="flex items-center gap-2">
            {rating && (
              <span className="inline-flex items-center gap-0.5 text-tbc-gold text-xs">
                <Star className="w-3 h-3 fill-tbc-gold" />
                {rating}
                {product?.business?.review_count > 0 && (
                  <span className="text-white/50 ml-0.5">
                    ({product.business.review_count})
                  </span>
                )}
              </span>
            )}
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-tbc-gold text-tbc-navy"
              aria-hidden
            >
              <ShoppingBag className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ---- Explore Black-Owned category tile --------------------------------
export function CategoryTile({ cat, className = "" }) {
  if (!cat) return null;
  return (
    <Link
      to={`/blinterest/discover?cat=${cat.slug}`}
      className={`group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-tbc-gold/50 transition-colors ${className}`}
      data-testid={`category-tile-${cat.slug}`}
    >
      <div className="aspect-square overflow-hidden bg-white/5">
        {cat.image_url ? (
          <img
            src={resolveAssetUrl(cat.image_url)}
            alt={cat.label}
            className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-tbc-gold/30 to-tbc-navy/60" />
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-tbc-navy/95 via-tbc-navy/60 to-transparent p-2 pt-6">
        <p className="text-white text-xs font-heading text-center">{cat.label}</p>
      </div>
    </Link>
  );
}

// ---- Shop the Moment editorial card -----------------------------------
export function MomentCard({ moment, className = "" }) {
  if (!moment) return null;
  return (
    <Link
      to={`/blinterest/discover?cat=${moment.category_slug || ""}`}
      className={`group relative rounded-2xl overflow-hidden bg-tbc-cream border border-tbc-gold/20 hover:border-tbc-gold/60 transition-colors ${className}`}
      data-testid={`moment-card-${moment.slug}`}
    >
      <div className="aspect-[4/5] relative overflow-hidden">
        {moment.cover_image ? (
          <img
            src={resolveAssetUrl(moment.cover_image)}
            alt={moment.label}
            className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-tbc-gold/40 to-tbc-cream" />
        )}
        {/* Legibility scrim — flips label contrast regardless of source image */}
        <div className="absolute inset-0 bg-gradient-to-t from-tbc-cream via-tbc-cream/85 to-tbc-cream/25" />
        <div className="absolute inset-0 flex flex-col justify-between p-3">
          <span className="text-2xl leading-none drop-shadow" aria-hidden>{moment.emoji}</span>
          <div>
            <p className="font-heading text-tbc-navy text-sm sm:text-base leading-tight">
              {moment.label}
            </p>
            <p className="text-tbc-navy/80 text-[11px] mt-0.5">Shop now</p>
          </div>
        </div>
      </div>
    </Link>
  );
}
